import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Plane, BedDouble, Utensils, Ticket, Bus, Coins, PiggyBank, ArrowRightLeft, AlertTriangle, Wand2, Loader2, Check, Info } from "lucide-react";

const CATS = [
  { key: "flights", label: "Flights", icon: Plane },
  { key: "accommodation", label: "Accommodation", icon: BedDouble },
  { key: "food", label: "Food & drink", icon: Utensils },
  { key: "attractions", label: "Attractions", icon: Ticket },
  { key: "local_transport", label: "Local transport", icon: Bus },
  { key: "miscellaneous", label: "Miscellaneous", icon: Coins },
];

const numFrom = (v) => {
  const m = String(v ?? "").replace(/,/g, "").match(/\d+(\.\d+)?/);
  return m ? Number(m[0]) : 0;
};

const derivePlan = (trip) => {
  if (trip.budget_plan) return trip.budget_plan;
  const cb = trip.cost_breakdown || {};
  const req = trip._request || {};
  const estimates = {
    flights: 0,
    accommodation: numFrom(cb.accommodation),
    food: numFrom(cb.food),
    attractions: numFrom(cb.activities),
    local_transport: numFrom(cb.transport),
    miscellaneous: 0,
  };
  const sum = Object.values(estimates).reduce((a, b) => a + b, 0);
  const total = numFrom(req.target_budget) || numFrom(cb.total) || sum;
  return { currency: (cb.currency || "USD").toUpperCase(), total, estimates };
};

export const BudgetPlanner = ({ trip, editable = false, onTripChange }) => {
  const [plan, setPlan] = useState(() => derivePlan(trip));
  const [fx, setFx] = useState(null);
  const [opt, setOpt] = useState(null);
  const [optLoading, setOptLoading] = useState(false);
  const saveTimer = useRef();
  const fxTimer = useRef();

  // Keep local plan in sync if the trip changes underneath (e.g. undo/restore).
  useEffect(() => {
    if (trip.budget_plan) setPlan(trip.budget_plan);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trip.budget_plan]);

  const spent = useMemo(
    () => CATS.reduce((s, c) => s + numFrom(plan.estimates?.[c.key]), 0),
    [plan]
  );
  const total = numFrom(plan.total);
  const remaining = total - spent;
  const pct = total > 0 ? Math.min(999, Math.round((spent / total) * 100)) : 0;
  const over = remaining < 0;

  const fmt = (n) => {
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: (plan.currency || "USD").toUpperCase(),
        maximumFractionDigits: 0,
      }).format(n);
    } catch {
      return `${plan.currency || ""} ${Math.round(n)}`;
    }
  };

  const persist = (next) => {
    setPlan(next);
    if (!onTripChange) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => onTripChange({ ...trip, budget_plan: next }, { label: "Budget update" }), 900);
  };

  const setCat = (key, val) =>
    persist({ ...plan, estimates: { ...plan.estimates, [key]: numFrom(val) } });
  const setTotal = (val) => persist({ ...plan, total: numFrom(val) });

  const topDrivers = [...CATS]
    .sort((a, b) => numFrom(plan.estimates?.[b.key]) - numFrom(plan.estimates?.[a.key]))
    .slice(0, 2)
    .filter((c) => numFrom(plan.estimates?.[c.key]) > 0)
    .map((c) => c.label);

  const optimize = async () => {
    setOptLoading(true);
    try {
      const { data } = await api.post("/trips/budget-optimize", { trip });
      setOpt(data);
    } catch {
      toast.error("Couldn't optimise the budget right now.");
    } finally {
      setOptLoading(false);
    }
  };
  const applyEstimates = () => {
    if (!opt?.updated_estimates) return;
    const est = {};
    CATS.forEach((c) => (est[c.key] = numFrom(opt.updated_estimates[c.key] ?? plan.estimates?.[c.key])));
    persist({ ...plan, estimates: est });
    setOpt(null);
    toast.success("Applied the suggested estimates");
  };

  // Live FX conversion of the total budget to USD + AUD.
  useEffect(() => {
    clearTimeout(fxTimer.current);
    if (!total) { setFx(null); return; }
    fxTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/fx/convert`, {
          params: { amount: total, base: (plan.currency || "USD").toUpperCase(), targets: "USD,AUD" },
        });
        setFx(data);
      } catch {
        setFx(null);
      }
    }, 500);
    return () => clearTimeout(fxTimer.current);
  }, [total, plan.currency]);

  return (
    <section data-testid="budget-planner">
      <h3 className="font-display text-3xl mb-2 flex items-center gap-3">
        <PiggyBank className="w-6 h-6 text-[#C8553D]" /> Budget planner
      </h3>
      <p className="text-muted-foreground mb-6">Set your total budget and fine-tune each estimate. Pre-filled from your plan — tweak any line. <span className="inline-flex items-center gap-1 text-xs"><Info className="w-3 h-3" /> Figures are estimates, not guaranteed prices.</span></p>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Summary */}
        <div className="lg:col-span-5 p-8 rounded-2xl bg-[#1A3B2B] text-[#FDFBF7]">
          <label className="text-xs uppercase tracking-widest opacity-80 mb-2 block">Total trip budget</label>
          <div className="flex items-center gap-2">
            <span className="font-display text-2xl opacity-80">{(plan.currency || "USD")}</span>
            <input
              data-testid="budget-total-input"
              type="number"
              min="0"
              value={plan.total ?? ""}
              onChange={(e) => setTotal(e.target.value)}
              disabled={!onTripChange}
              className="flex-1 min-w-0 bg-transparent border-b border-[#FDFBF7]/30 focus:border-[#C8553D] outline-none font-display text-4xl py-1"
            />
          </div>

          {fx?.conversions?.length > 0 && (
            <div data-testid="budget-fx" className="mt-4 flex flex-wrap gap-2">
              {fx.conversions.map((c) => (
                <span key={c.currency} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FDFBF7]/10 text-sm">
                  <ArrowRightLeft className="w-3.5 h-3.5" /> ≈ {c.currency} {new Intl.NumberFormat().format(Math.round(c.amount))}
                </span>
              ))}
              {fx.rate_date && <span className="text-xs opacity-70 self-center">rates {fx.rate_date}{fx.stale ? " (cached)" : ""}</span>}
            </div>
          )}

          <div className="mt-6 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="opacity-80">Estimated spend</span>
              <span data-testid="budget-spent" className="font-semibold">{fmt(spent)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="opacity-80">Remaining</span>
              <span data-testid="budget-remaining" className={`font-semibold ${over ? "text-[#F0A58F]" : "text-[#FDFBF7]"}`}>{fmt(remaining)}</span>
            </div>
            <div>
              <div className="flex items-center justify-between text-xs mb-1.5 opacity-80">
                <span>{pct}% of budget used</span>
                {over && <span className="text-[#F0A58F]">Over budget</span>}
              </div>
              <div className="h-2.5 w-full rounded-full bg-[#FDFBF7]/15 overflow-hidden">
                <div
                  data-testid="budget-progress"
                  className={`h-full transition-all duration-300 ${over ? "bg-[#C8553D]" : "bg-[#C8553D]"}`}
                  style={{ width: `${Math.min(100, pct)}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Category estimates */}
        <div className="lg:col-span-7 p-6 rounded-2xl border border-black/5 bg-white/60">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {CATS.map((c) => (
              <div key={c.key} className="flex items-center gap-3 p-3 rounded-xl border border-black/5 bg-[#FDFBF7]">
                <c.icon className="w-5 h-5 text-[#1A3B2B] shrink-0" strokeWidth={1.75} />
                <div className="min-w-0 flex-1">
                  <label className="text-xs uppercase tracking-wide text-muted-foreground block">{c.label}</label>
                  <div className="flex items-center gap-1">
                    <span className="text-sm text-muted-foreground">{plan.currency}</span>
                    <input
                      data-testid={`budget-cat-${c.key}`}
                      type="number"
                      min="0"
                      value={plan.estimates?.[c.key] ?? 0}
                      onChange={(e) => setCat(c.key, e.target.value)}
                      disabled={!onTripChange}
                      className="w-full min-w-0 bg-transparent border-b border-transparent focus:border-[#C8553D] outline-none font-medium text-lg py-0.5"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
          {!onTripChange && (
            <p className="mt-4 text-xs text-muted-foreground">Open this trip from your saved trips to edit the budget.</p>
          )}
        </div>
      </div>

      {/* Over-budget alert + optimiser */}
      {over && (
        <div data-testid="budget-alert" className="mt-5 rounded-2xl border border-[#C8553D]/40 bg-[#C8553D]/5 p-5">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-[#C8553D] shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-[#C8553D]">Over budget by {fmt(-remaining)}</p>
                {topDrivers.length > 0 && (
                  <p className="text-sm text-muted-foreground mt-0.5">Biggest drivers: {topDrivers.join(" & ")}. Let WanderlyMe suggest verified cheaper swaps.</p>
                )}
              </div>
            </div>
            {onTripChange && (
              <button
                data-testid="budget-optimize-btn"
                onClick={optimize}
                disabled={optLoading}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1A3B2B] text-[#FDFBF7] text-sm hover:bg-[#12291e] transition-colors disabled:opacity-60 shrink-0"
              >
                {optLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                Optimise my budget
              </button>
            )}
          </div>

          {opt && (
            <div data-testid="budget-suggestions" className="mt-4 rounded-xl bg-white/70 border border-black/5 p-4">
              {opt.summary && <p className="text-sm mb-3">{opt.summary}</p>}
              <ul className="space-y-2">
                {(opt.suggestions || []).map((s, i) => (
                  <li key={i} className="flex items-start justify-between gap-3 text-sm border-b border-black/5 pb-2 last:border-0">
                    <span>
                      <span className="text-xs uppercase tracking-wide text-[#C8553D] mr-2">{s.category}</span>
                      {s.change}
                      {s.note ? <span className="block text-xs text-muted-foreground mt-0.5">{s.note}</span> : null}
                    </span>
                    {s.est_saving ? <span className="text-[#1A3B2B] font-medium whitespace-nowrap">~ save {fmt(numFrom(s.est_saving))}</span> : null}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1"><Info className="w-3 h-3" /> Savings are approximate estimates, not guaranteed prices.</p>
              {opt.updated_estimates && (
                <button
                  data-testid="budget-apply-estimates"
                  onClick={applyEstimates}
                  className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#1A3B2B] text-[#1A3B2B] text-sm hover:bg-[#1A3B2B] hover:text-[#FDFBF7] transition-colors"
                >
                  <Check className="w-4 h-4" /> Apply suggested estimates
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
};
